import type { TranslationKey } from './en';

/**
 * 简体中文 copy.
 *
 * Same keys as `en.ts`, in the same order, so the two can be diffed line for
 * line. `Record<TranslationKey, string>` means a key added to English but not
 * here is a compile error rather than an English string appearing mid-sentence.
 */
export const zh: Record<TranslationKey, string> = {
  /* ── App chrome ─────────────────────────────────────────────────────── */
  'app.name': '后台管理',
  'app.tagline': '彩票运营',
  'app.footer.version': '彩票后台 v0.1.0',
  'app.footer.api': '接口',
  'app.footer.connections': '{count} 个在线连接',
  'app.footer.connections_plural': '{count} 个在线连接',
  'app.footer.uptime': '运行时长 {value}',
  'app.footer.demo': '',
  'app.signOut': '退出登录',
  'app.theme.toLight': '切换到浅色主题',
  'app.theme.toDark': '切换到深色主题',
  'app.language': '语言',
  'app.openNav': '打开导航',
  'app.closeNav': '关闭导航',
  'app.sessionEnds': '登录将在 {minutes} 分钟后结束',

  /* ── Navigation ─────────────────────────────────────────────────────── */
  'nav.group.watch': '监控',
  'nav.group.people': '人员',
  'nav.group.money': '资金',
  'nav.group.control': '设置',
  'nav.dashboard': '仪表板',
  'nav.draws': '开奖期数',
  'nav.games': '游戏',
  'nav.review': '待审核',
  'nav.players': '玩家',
  'nav.testPlayers': '测试玩家',
  'nav.admins': '管理员',
  'nav.sessions': '登录记录',
  'nav.bets': '投注记录',
  'nav.ledger': '资金流水',
  'nav.reports': '报表',
  'nav.referrals': '邀请码',
  'nav.notifications': '通知文案',
  'nav.settings': '账号设置',
  'nav.permissions': '权限管理',
  'permissions.note': '设置各角色可执行的操作。保存后立即生效——正在登录的操作员无需刷新，菜单与权限会同步更新。',
  'permissions.superadminNote': '超级管理员不在此列：其始终拥有全部权限，且「管理员」与本页面本身不属于可配置权限。这样才能确保任何保存都不会把所有人锁在门外。',
  'permissions.module': '模块',
  'permissions.live': '实时',
  'permissions.editing': '编辑中',
  'permissions.changedFromDefault': '（已改为非默认）',
  'permissions.resetAction': '恢复默认',
  'permissions.resetPrompt': '确定将此角色恢复为出厂默认权限？',
  'permissions.discardPrompt': '确定放弃对此角色尚未保存的修改？',
  'permissions.saved': '权限已保存',
  'permissions.savedDetail': '所有{role}的控制台已同步更新。',
  'permissions.reset': '权限已重置',
  'permissions.resetDetail': '该角色已恢复为出厂默认。',
  'permissions.action.view': '查看',
  'permissions.action.create': '新增',
  'permissions.action.update': '修改',
  'permissions.action.delete': '删除',
  'nav.audit': '操作日志',

  /* ── Common actions and states ──────────────────────────────────────── */
  'action.search': '搜索',
  'action.reset': '重置',
  'action.refresh': '刷新',
  'action.cancel': '取消',
  'action.save': '保存修改',
  'action.create': '创建',
  'action.edit': '编辑',
  'action.delete': '删除',
  'action.remove': '移除',
  'action.approve': '通过',
  'action.reject': '拒绝',
  'action.copy': '复制',
  'action.withdraw': '提现',
  'action.revoke': '结束登录',
  'action.discard': '放弃修改',
  'action.deletePermanently': '永久删除',

  'common.filters': '筛选',
  'common.filtersActive': '已启用 {count} 项',
  'common.filtersUnapplied': '尚未应用 — 请点击搜索',
  'common.perPage': '每页',
  'common.showing': '显示',
  'common.of': '共',
  'common.page': '第',
  'common.noRecords': '暂无记录',
  'common.first': '首页',
  'common.previous': '上一页',
  'common.next': '下一页',
  'common.last': '末页',
  'common.reason': '原因',
  'common.reasonHint': '将记录到操作日志',
  'common.required': '必填',
  'common.never': '永不',
  'common.none': '—',
  'common.justNow': '刚刚',
  'common.ago': '{value}前',
  'common.in': '{value}后',
  'common.retry': '重试',
  'common.loading': '加载中',
  'common.anyStatus': '全部状态',
  'common.username': '用户名',
  'common.fullName': '姓名',
  'common.phone': '电话',
  'common.balance': '余额',
  'common.status': '状态',
  'common.created': '创建时间',
  'common.joined': '注册时间',
  'common.lastSeen': '最近活动',
  'common.when': '时间',
  'common.amount': '金额',
  'common.type': '类型',
  'common.player': '玩家',
  'common.round': '期号',
  'common.period': '期数',
  'common.from': '开始',
  'common.to': '结束',
  'common.state': '状态',
  'common.role': '角色',
  'common.actions': '操作',
  'common.referralCode': '邀请码',
  'common.roundNo': '第 {drawNo} 期',

  /* ── Statuses ───────────────────────────────────────────────────────── */
  'status.pending_review': '待审核',
  'status.active': '正常',
  'status.banned': '已封禁',
  'status.rejected': '已拒绝',
  'status.open': '进行中',
  'status.locked': '已封盘',
  'status.drawn': '已开奖',
  'status.settled': '已结算',
  'status.void': '已作废',
  'status.placed': '已投注',
  'status.won': '中奖',
  'status.lost': '未中奖',
  'status.refunded': '已退款',
  'status.voided': '已作废',
  'status.success': '成功',
  'status.processing': '处理中',
  'status.pending': '待处理',
  'status.failed': '失败',
  'status.idle': '闲置超时',
  'status.expired': '已过期',
  'status.revoked': '已结束',
  'status.test': '测试',
  'status.manual': '人工',

  /* ── Sign in ────────────────────────────────────────────────────────── */
  'signIn.title': '登录',
  'signIn.subtitle': '仅限内部人员。此处的每一项操作都会记入操作日志。',
  'signIn.username': '用户名',
  'signIn.password': '密码',
  'signIn.submit': '登录',
  'signIn.failed': '登录失败，请确认接口服务是否已启动。',
  'signIn.seeded': '',
  'signIn.expired': '登录已超时，请重新登录。',
  'signIn.rejected': '登录状态已失效，请重新登录。',
  'signIn.idle': '因长时间未操作，您已被退出登录。',
  'signIn.superseded': '该账号已在其他设备登录，您在此处的会话已被结束。若非本人操作，请立即修改密码。',
  'signIn.displaced': '登录成功。该账号原本已有一个会话，现已被结束。',
  'signIn.displacedDetail': '若非本人操作，请立即修改密码。',

  /* ── Dashboard ──────────────────────────────────────────────────────── */
  'dashboard.liveRound': '当前期数',
  'dashboard.gamePaused': '游戏已暂停',
  'dashboard.remaining': '剩余',
  'dashboard.bettingShut': '已封盘',
  'dashboard.bettingShutsIn': '{value} 后封盘',
  'dashboard.justDrawn': '刚刚开奖',
  'dashboard.resultAnnounce': '第 {period} 期开出 {numbers}',
  'dashboard.revealQueued': '还有 {count} 期结果等待展示',
  'dashboard.revealQueued_plural': '还有 {count} 期结果等待展示',
  'dashboard.roundsStopped': '已停止开奖',
  'dashboard.everyInterval': '每 {value}',
  'dashboard.gameSettings': '游戏设置',
  'dashboard.latestNumbers': '最新开奖号码',
  'dashboard.allDraws': '全部期数',
  'dashboard.noSettled': '暂无已结算的期数。',
  'dashboard.turnover': '总投注额',
  'dashboard.paidOut': '总派彩',
  'dashboard.grossProfit': '毛利',
  'dashboard.awaitingReview': '待审核',
  'dashboard.settledBets': '已结算 {count} 笔',
  'dashboard.acrossRounds': '共 {count} 期',
  'dashboard.margin': '利润率 {value}%',
  'dashboard.needsOperator': '需要人工处理',
  'dashboard.queueClear': '暂无待办',
  'dashboard.waitingApproval': '等待审核',
  'dashboard.openQueue': '打开队列',
  'dashboard.nothingWaiting': '暂无待审核',
  'dashboard.nothingWaitingDetail': '新注册的玩家会自动出现在这里。',
  'dashboard.system': '系统状态',
  'dashboard.liveConnections': '在线连接',
  'dashboard.playersOnline': '在线玩家',
  'dashboard.staffOnline': '在线员工',
  'dashboard.onlineNow': '当前在线',
  'dashboard.nobodyOnline': '当前无人在线',
  'dashboard.account': '账号',
  'dashboard.connections': '连接数',
  'dashboard.staff': '员工',
  'dashboard.responseCache': '响应缓存',
  'dashboard.recentActivity': '近期动态',

  /* ── Sessions ───────────────────────────────────────────────────────── */
  'sessions.title': '登录记录',
  'sessions.eyebrow': '访问控制',
  'sessions.note': '每次登录都会生成一条记录。结束后，该登录的下一次请求将被拒绝，令牌本身也随之失效。',
  'sessions.principal': '账号',
  'sessions.kind': '类型',
  'sessions.startedAt': '登录时间',
  'sessions.expiresAt': '结束时间',
  'sessions.ip': 'IP 地址',
  'sessions.revokeTitle': '结束该登录',
  'sessions.revokeBody': '{username} 的下一次请求将被拒绝。若账号本身未被停用，仍可重新登录。',
  'sessions.revokeAll': '结束全部登录',
  'sessions.empty': '没有符合条件的登录记录',
  'sessions.emptyDetail': '请调整筛选条件后重新搜索。',
  'sessions.ended': '登录已结束',
  'sessions.endedCount': '已结束 {count} 条登录',
  'sessions.anyKind': '玩家与员工',
  'sessions.admin': '员工',
  'sessions.player': '玩家',

  /* ── Games ──────────────────────────────────────────────────────────── */
  'games.playable': '可玩游戏',
  'games.running': '运行中',
  'games.paused': '已暂停',
  'games.pause': '暂停开奖',
  'games.start': '开始开奖',
  'games.settings': '设置',
  'games.currentRound': '当前期数',
  'games.untilCloses': '后封盘',
  'games.untilBetsClose': '后停止投注',
  'games.inLockdown': '已封盘 — 不再接受投注',
  'games.opening': '正在开启下一期…',
  'games.noRound': '当前没有进行中的期数。开启游戏即可开始循环。',
  'games.lastResult': '上期结果',
  'games.numbersPerRound': '每期号码数量',
  'games.roundEvery': '开奖间隔',
  'games.lockdown': '开奖前封盘时长',
  'games.lockdownOff': '无 — 投注至截止',
  'games.numberRange': '号码范围',
  'games.odds': '赔率',
  'games.roundsSoFar': '累计期数',
  'games.inHall': '游戏大厅',
  'games.visible': '可见且可点击',
  'games.hidden': '隐藏',
  'games.otherTiles': '其他游戏',
  'games.gameHall': '游戏大厅',
  'games.otherNote':
    '这些游戏会出现在玩家端大厅，但背后没有任何逻辑运行 — 按需求文档只有彩票是可玩的。放在这里是为了排列大厅，而非用于启动。',
  'games.applyNote':
    '修改仅对此后新开的期数生效。已在进行中的期数会保留开启时的设置 — 改写这些设置会改变已投注玩家当时的投注依据。',
  'games.started': '游戏已开启',
  'games.startedDetail': '下一次循环即会开启新一期。',
  'games.pausedToast': '游戏已暂停',
  'games.pausedDetail': '进行中的期数仍会正常结算，之后停止开奖。',

  'common.entries': '{count} 条',
  'time.dh': '{days} 天 {hours} 小时',
  'time.hm': '{hours} 小时 {minutes} 分钟',

  /* ── Relative time ──────────────────────────────────────────────────── */
  'time.seconds': '{value} 秒',
  'time.minutes': '{value} 分钟',
  'time.hours': '{value} 小时',
  'time.days': '{value} 天',

  /* ── Draws ──────────────────────────────────────────────────────────── */
  'draws.roundDetail': '{positions} 个号码 · {closesAt} 封盘',
  'draws.opened': '开盘时间',
  'draws.drawn': '开奖时间',
  'draws.source': '来源',
  'draws.auto': '自动',
  'draws.you': '本人',

  /* ── Shared vocabulary ───────────────────────────────────────────────── */
  'common.save': '保存',
  'common.generate': '生成',
  'common.optional': '选填',
  'common.noMatch': '请调整筛选条件后重新搜索。',
  'common.ledgerHint': '将记录在账目明细与操作日志中',
  'common.typeToConfirm': '请输入 {word} 以确认',
  'common.any': '全部',

  /* ── Form fields ─────────────────────────────────────────────────────── */
  'field.address': '地址',
  'field.password': '密码',
  'field.resetPassword': '重置密码',
  'field.keepPassword': '留空则保持原密码',
  'field.minPassword': '至少 8 个字符',
  'field.displayName': '显示名称',
  'field.openingBalance': '初始余额',
  'field.note': '备注',
  'field.locale': '语言',
  'field.usernameHint': '仅限字母、数字与下划线',
  'field.verifiedByPhone': '已通过电话验证',

  /* ── Table columns ───────────────────────────────────────────────────── */
  'col.lastSignIn': '最近登录',
  'col.reference': '流水号',
  'col.stake': '投注额',
  'col.payout': '派彩',
  'col.registered': '注册时间',
  'col.target': '对象',
  'col.change': '变更',
  'col.operator': '操作人',
  'col.action': '操作',
  'col.balanceAfter': '变更后余额',
  'col.result': '结果',
  'col.odds': '赔率',
  'col.placed': '下注时间',
  'col.pos': '位置',
  'col.bet': '玩法',
  'col.numbers': '开奖号码',
  'col.bets': '注数',
  'col.dateTime': '日期时间',
  'col.winLoss': '盈亏',
  'col.code': '号码',
  'col.usage': '使用情况',
  'col.expires': '到期',
  'col.staked': '投注总额',
  'col.paidOut': '派彩总额',

  /* ── Section eyebrows ────────────────────────────────────────────────── */
  'section.people': '人员',
  'section.money': '资金',
  'section.control': '管理',
  'section.record': '记录',
  'section.rounds': '期数',
  'section.review': '审核',
  'section.credits': '额度',
  'section.staff': '员工',
  'section.account': '账户',
  'section.games': '游戏',
  'section.promise': '原则',

  /* ── Audit actions ───────────────────────────────────────────────────── */
  'audit.user.approve': '通过注册审核',
  'audit.user.reject': '拒绝注册',
  'audit.user.create': '创建玩家',
  'audit.user.update': '修改玩家',
  'audit.user.ban': '封禁玩家',
  'audit.user.delete': '删除玩家',
  'audit.user.balance_set': '设置余额',
  'audit.user.top_up': '充值',
  'audit.user.withdraw': '提现',
  'audit.user.history_generate': '生成历史记录',
  'audit.bet.update': '修改注单',
  'audit.bet.delete': '删除注单',
  'audit.draw.auto_result': '自动开奖',
  'audit.draw.manual_result': '人工开奖',
  'audit.draw.result_edit': '更改开奖结果',
  'audit.draw.void': '作废期次',
  'audit.draw.delete': '删除期次',
  'audit.transaction.update': '修改账目',
  'audit.transaction.delete': '删除账目',
  'audit.admin.create': '创建管理员',
  'audit.admin.update': '修改管理员',
  'audit.admin.delete': '删除管理员',
  'audit.admin.password_change': '修改密码',
  'audit.session.revoke': '结束会话',
  'audit.settings.update': '修改设置',
  'audit.referral_code.create': '创建推荐码',
  'audit.referral_code.update': '修改推荐码',
  'audit.referral_code.delete': '删除推荐码',
  'audit.game.create': '新增游戏',
  'audit.game.update': '修改游戏',
  'audit.game.delete': '删除游戏',
  'audit.testdata.generate': '生成测试数据',

  /* ── Wallet transaction types ────────────────────────────────────────── */
  'tx.top_up': '充值',
  'tx.withdrawal': '提现',
  'tx.bet_stake': '投注扣款',
  'tx.bet_payout': '中奖派彩',
  'tx.bet_refund': '投注退款',
  'tx.credit_adjustment': '额度调整',
  'tx.admin_adjustment': '人工调整（已停用）',

  /* ── Bet types ───────────────────────────────────────────────────────── */
  'bet.big': '大',
  'bet.small': '小',
  'bet.odd': '单',
  'bet.even': '双',

  /* ── Players ─────────────────────────────────────────────────────────── */
  'players.title': '玩家',
  'players.new': '新增玩家',
  'players.empty': '没有符合条件的玩家',
  'players.credit': '额度',
  'players.filter.username': '用户名',
  'players.filter.usernamePlaceholder': '搜索用户名',
  'players.create.title': '新增玩家',
  'players.create.submit': '创建玩家',
  'players.edit.title': '编辑 {name}',
  'players.delete.title': '删除 {name}',
  'players.delete.warning': '此操作将删除该玩家及其全部注单与钱包记录，且无法撤销。',
  'players.detail.wallet': '钱包记录',
  'players.detail.bets': '投注记录',
  'players.detail.walletBalance': '钱包余额',
  'players.detail.adjustCredit': '调整额度',
  'players.detail.editProfile': '编辑资料与状态',
  'players.detail.deletePlayer': '删除玩家',
  'players.detail.needAdmin': '增加额度与编辑资料需要管理员权限。',
  'players.detail.noWallet': '暂无钱包记录',
  'players.detail.noWalletDetail': '充值、投注与派彩将显示在此处。',
  'players.detail.noBets': '暂无投注',
  'players.detail.approved': '审核通过',

  /* ── Review queue ────────────────────────────────────────────────────── */
  'review.title': '待审核注册',
  'review.empty': '暂无待办',
  'review.emptyDetail': '所有注册均已处理，新的注册会自动出现在此处。',
  'review.approve': '通过',
  'review.reject': '拒绝',
  'review.approveTitle': '通过 {name}',
  'review.approveSubmit': '通过并开通账户',
  'review.rejectTitle': '拒绝 {name}',
  'review.rejectSubmit': '拒绝注册',
  'review.rejectPlaceholder': '重复注册',

  /* ── Test players ────────────────────────────────────────────────────── */
  'testPlayers.title': '可自定义的玩家',
  'testPlayers.subtitle': '先创建玩家，再为其生成历史记录。',
  'testPlayers.new': '新增测试玩家',
  'testPlayers.empty': '暂无玩家',
  'testPlayers.emptyDetail': '请先创建玩家，再为其生成投注记录。',
  'testPlayers.createNote': '默认创建为启用状态，可立即生成历史记录。',
  'testPlayers.history': '历史',
  'testPlayers.historyTitle': '为 {name} 生成历史',
  'testPlayers.rounds': '覆盖期数',
  'testPlayers.betsPerRound': '每期注数',
  'testPlayers.minStake': '最低投注额',
  'testPlayers.maxStake': '最高投注额',
  'testPlayers.bias': '中奖倾向',
  'testPlayers.biasHint': '保持“均衡”即为随机分布。此选项只决定投注方向，结果仍按各期真实开奖判定。',
  'testPlayers.biasBalanced': '均衡（随机）',
  'testPlayers.biasMostlyWin': '多数中奖（80%）',
  'testPlayers.biasSlightWin': '略微中奖（60%）',
  'testPlayers.biasSlightLose': '略微亏损（40%）',
  'testPlayers.biasMostlyLose': '多数亏损（20%）',

  /* ── Bets ────────────────────────────────────────────────────────────── */
  'bets.title': '注单审核',
  'bets.empty': '没有符合条件的注单',
  'bets.filter.roundNumber': '期号',
  'bets.filter.playerName': '玩家名称',
  'bets.filter.playerId': '玩家 ID',
  'bets.edit.title': '修改注单',
  'bets.edit.round': '第 {drawNo} 期',
  'bets.edit.settledWarning': '该期已开奖。保存后将按开奖号码重新判定此注单，并同步调整玩家余额。',
  'bets.edit.openNote': '该期尚未截止。投注额的差额将像其他余额变动一样计入玩家钱包。',
  'bets.edit.position': '位置',
  'bets.edit.positionRange': '位置 — 1 至 {count}',
  'bets.edit.positionError': '超出该游戏的位置范围',
  'bets.edit.betType': '玩法',
  'bets.edit.stake': '投注额',
  'bets.edit.stakeError': '请输入大于零的金额',
  'bets.edit.takesFurther': '将再从玩家扣除 {amount}。',
  'bets.edit.returns': '将退还 {amount} 给玩家。',
  'bets.edit.reasonPlaceholder': '客服来电后更正',
  'bets.remove.title': '删除注单',
  'bets.remove.submit': '删除并冲正',
  'bets.remove.heading': '将冲正的内容',
  'bets.remove.reasonPlaceholder': '玩家反馈重复提交',

  /* ── Draws ───────────────────────────────────────────────────────────── */
  'draws.title': '开奖与号码',
  'draws.empty': '没有符合条件的期次',
  'draws.notDrawn': '未开奖',
  'draws.roundsMatched': '匹配期数',
  'draws.takenFromPlayers': '玩家投注',
  'draws.returnedToWinners': '派彩给中奖者',
  'draws.stakedMinusPaid': '投注额减派彩',
  'draws.schedulerNote': '系统每五分钟自动开出一期。',
  'draws.filter.period': '期次',
  'draws.filter.result': '开奖状态',
  'draws.filter.anyResult': '全部（以庄家角度计算）',
  'draws.filter.closedFrom': '截止起始',
  'draws.filter.closedTo': '截止结束',
  'draws.setResult': '录入结果',
  'draws.changeResult': '更改结果',
  'draws.setTitle': '录入第 {drawNo} 期结果',
  'draws.changeTitle': '更改第 {drawNo} 期结果',
  'draws.correctionEyebrow': '更正 · 该期已派彩',
  'draws.setSubmit': '录入并结算',
  'draws.changeSubmit': '更改并重新判定',
  'draws.freshNote': '该期所有注单将按此号码判定并立即派彩。',
  'draws.paidWarning': '该期已完成派彩。更改号码将重新判定其全部注单。',
  'draws.paidDetail': '派彩差额将以调整方式计入钱包：原本中奖而现在未中的注单会退回款项，原本未中而现在中奖的注单会全额派彩。余额始终保持一致，玩家可在记录中看到变动。',
  'draws.currentResult': '当前开奖号码',
  'draws.newNumbers': '新号码',
  'draws.winningNumbers': '开奖号码',
  'draws.numbersHint': '{label} — 共 {count} 位，范围 {min} 至 {max}',
  'draws.unchanged': '这些就是当前号码，请至少修改一位。',
  'draws.acknowledge': '我了解此操作将冲正并重新派发真实彩金，且会记录在我的账户名下。',
  'draws.reasonCorrect': '开奖记录有误 — 已更正',
  'draws.reasonSheet': '依据开奖表录入',
  'draws.void': '作废',
  'draws.voidTitle': '作废第 {drawNo} 期',
  'draws.voidSubmit': '作废并退款',
  'draws.voidReason': '该期调度异常',
  'draws.deleteTitle': '删除第 {drawNo} 期',
  'draws.deleteSubmit': '删除并冲正',
  'draws.deleteReason': '测试期间创建的重复期次',
  'draws.position': '位置',

  /* ── Wallet ledger ───────────────────────────────────────────────────── */
  'ledger.title': '钱包账目',
  'ledger.empty': '没有符合条件的账目',
  'ledger.filter.anyType': '全部类型',
  'ledger.filter.anyStatus': '全部状态',
  'ledger.edit.title': '修改账目',
  'ledger.delete.title': '删除账目',
  'ledger.delete.submit': '删除并校正余额',

  /* ── Referral codes ──────────────────────────────────────────────────── */
  'referrals.title': '推荐码',
  'referrals.empty': '暂无推荐码',
  'referrals.emptyDetail': '玩家必须凭推荐码注册，请至少生成一个。',
  'referrals.generate': '生成推荐码',
  'referrals.generateTitle': '生成推荐码',
  'referrals.codeHint': '留空则自动生成',
  'referrals.codeError': '至少 4 个字符',
  'referrals.codePlaceholder': '自动',
  'referrals.noteHint': '用途说明 — 仅在后台可见',
  'referrals.notePlaceholder': 'VIP 开户',
  'referrals.maxUses': '最大使用次数',
  'referrals.expires': '到期时间',
  'referrals.expiresHint': '留空表示永不过期',
  'referrals.editTitle': '编辑 {code}',
  'referrals.deleteTitle': '删除 {code}',
  'referrals.deleteSubmit': '删除推荐码',
  'referrals.usedBy': '仍在使用该推荐码的账户',
  'referrals.used': '已使用',
  'referrals.remaining': '剩余',
  'referrals.usable': '可用',
  'referrals.usedUp': '已用完',
  'referrals.expired': '已过期',
  'referrals.disabled': '已停用',
  'referrals.active': '启用',
  'referrals.activeHint': '可用于注册',
  'referrals.inactiveHint': '使用该推荐码的注册将被拒绝',

  /* ── Administrators ──────────────────────────────────────────────────── */
  'admins.title': '管理员',
  'admins.empty': '没有符合条件的管理员',
  'admins.new': '新增管理员',
  'admins.createTitle': '新增管理员',
  'admins.editTitle': '编辑 {name}',
  'admins.deleteTitle': '删除 {name}',
  'admins.accountActive': '账户已启用',
  'admins.accountActiveHint': '已停用的账户无法登录。',
  'admins.cannotDeleteSelf': '无法删除自己的账户',
  'admins.selfNote': '这是你自己的账户，角色与状态已锁定 — 修改任一项都可能使组织失去最后一位超级管理员。如需改密码请前往“设置”。',
  'admins.role.superadmin': '超级管理员',
  'admins.role.admin': '管理员',
  'admins.role.support': '客服',
  'admins.roleDetail.superadmin': '拥有全部权限，包括管理管理员。',
  'admins.roleDetail.admin': '除管理管理员外的全部权限。',
  'admins.roleDetail.support': '只读权限，并可凭原因扣除额度。',

  /* ── Games ───────────────────────────────────────────────────────────── */
  'games.title': '游戏大厅',
  'games.playableGame': '可投注',
  'games.inPlayerHall': '玩家大厅',
  'games.settingsTitle': '设置 — {name}',
  'games.saveSettings': '保存设置',
  'games.settingsNote': '设置仅对此后开出的期次生效。进行中的期次仍沿用开盘时的设置 — 否则会改变已下注单的成交条件。',
  'games.name': '名称',
  'games.subtitle': '副标题',
  'games.numbersHint': '1 至 20',
  'games.numbersError': '须介于 1 与 20 之间',
  'games.interval': '每期间隔（秒）',
  'games.intervalHint': '每 {value} 开出一期',
  'games.intervalMin': '至少 30 秒',
  'games.intervalError': '须介于 30 与 86,400 之间',
  'games.lockdownHint': '开奖前 {value} 停止投注',
  'games.lockdownOffHint': '0 — 投注持续到本期截止',
  'games.lockdownMax': '此间隔下最多 {value}',
  'games.lockdownError': '必须短于每期间隔',
  'games.lowest': '最小号码',
  'games.highest': '最大号码',
  'games.rangeError': '必须大于最小号码',
  'games.oddsBp': '赔率（基点）',
  'games.oddsHint': '赔付 {value}',
  'games.oddsMin': '至少 10000（1.00×）',
  'games.oddsError': '至少 10000',
  /* ── 点击大厅卡片的行为 ─────────────────────────────────────────────── */
  'games.interactionLabel': '卡片状态',
  'games.interaction.clickable': '可进入',
  'games.interaction.disabled': '不可点击',
  'games.interaction.fake_sign_in': '会员专属',
  'games.interaction.coming_soon': '即将推出',
  'games.interactionHint.clickable': '可进入游戏，下方会显示期次设置。',
  'games.interactionHint.disabled': '在大厅中展示但不可点击，玩家点击无反应。',
  'games.interactionHint.fake_sign_in': '弹出永远无法通过的登录框，并提示玩家联系客服索取账号密码。',
  'games.interactionHint.coming_soon': '提示玩家该游戏即将推出。',
  'games.reasonPlaceholder': '为演示缩短开奖间隔',
  'games.pauseRounds': '暂停开奖',
  'games.startRounds': '开始开奖',

  /* 大厅图块 — 新增、编辑、隐藏、删除 */
  'games.addTile': '新增图块',
  'games.addTitle': '新增游戏大厅图块',
  'games.addSubmit': '新增图块',
  'games.addNote': '选择玩家点击该图块时的行为。新图块沿用预设的开奖设置，开始开奖则是另一个独立操作 — 编排大厅与运行游戏是两回事。',
  'games.noTiles': '大厅暂无其他图块',
  'games.noTilesDetail': '新增一个来充实玩家游戏大厅。',
  'games.roundSettings': '开奖设置',
  'games.slug': '标识（slug）',
  'games.slugHint': '小写并以连字符分隔 — 它是该游戏在 API 中的地址',
  'games.slugError': '仅限小写字母、数字与单个连字符',
  'games.slugLocked': '创建后不可更改 — 它既是 API 地址，也对应玩家端的图片资源',
  'games.badge': '角标',
  'games.badgeHint': '图块上的小标签，例如 New 或 VIP。留空则不显示。',
  'games.tileImage': '图块图片网址',
  'games.tileImageHint': '覆盖玩家端为此标识内置的图片',
  'games.sortOrder': '大厅中的排序',
  'games.sortOrderHint': '升序 — 0 排在最前',
  'games.sortOrderError': '须介于 0 与 9,999 之间',
  'games.hiddenToggle': '从大厅隐藏',
  'games.hiddenHint': '玩家完全收不到此图块 — API 与 WebSocket 皆不返回',
  'games.shownHint': '玩家可在游戏大厅看到此图块',
  'games.hiddenPlayableWarning': '隐藏并不会暂停游戏。排程仍会继续开出期次，进行中的期次也照常结算 — 把图块撤出大厅不应让已下的注单悬空。若你的本意是停盘，请同时暂停它。',
  'games.hiddenBadge': '已隐藏',
  'games.inHallBadge': '在大厅中',
  'games.show': '显示',
  'games.hide': '隐藏',
  'games.hiddenFromConsole': '于后台从大厅隐藏',
  'games.shownFromConsole': '于后台恢复至大厅',
  'games.deleteTitle': '删除 {name}',
  'games.deleteSubmit': '删除图块',
  'games.deleteNote': '将该图块从大厅永久移除。若改为隐藏，图块会退出玩家大厅，并可随时在本页恢复。',
  'games.deleteRunning': '该游戏仍在开出期次。请先暂停再删除。',
  'games.deleteHasRounds': '该游戏已有 {count} 期历史记录，无法删除 — 这些期次上的注单与账目仍指向它。请改为隐藏。',
  'games.deleteReasonPlaceholder': '该图块从未上线',

  /* ── Reports ─────────────────────────────────────────────────────────── */
  'reports.title': '经营概况',
  'reports.testAccounts': '测试账户',
  'reports.excluded': '已排除',
  'reports.included': '已包含',
  'reports.clearDates': '清除日期',
  'reports.turnover': '投注总额',
  'reports.turnoverHint': '已结算注单的投注总额',
  'reports.paidOut': '派彩总额',
  'reports.paidOutHint': '派发给中奖者',
  'reports.grossProfit': '毛利',
  'reports.roundsSettled': '已结算期数',
  'reports.meaning': '指标说明',
  'reports.reading': '如何解读',

  /* ── Notifications ───────────────────────────────────────────────────── */
  'notifications.title': '注册审核提示',
  'notifications.english': '英文',
  'notifications.chinese': '中文',
  'notifications.supportLink': '联系客服',
  'notifications.reviewUrl': '联系链接',
  'notifications.reviewUrlHint': '审核中的玩家可通过此链接咨询进度',
  'notifications.viewingHint': '实时同步服务器内容。点击「编辑」进行修改。',
  'notifications.editingHint': '编辑中。已暂停实时更新，不会覆盖您的输入。',
  'notifications.live': '实时',
  'notifications.editing': '编辑中',
  'notifications.contactUrl': '联系方式链接',
  'notifications.contactHint': '可填聊天链接、表单或 mailto: 地址',
  'notifications.reviewNum': '联系电话',
  'notifications.reviewNumHint': '待审核玩家可拨打或联系的号码。留空则不公开号码。',
  'notifications.contactNum': '联系电话',
  'notifications.contactNumHint': '用于充值与提现的联系号码。留空则不公开号码，玩家只会看到链接。',
  'notifications.save': '保存通知设置',
  'notifications.discard': '放弃更改',
  'notifications.players': '玩家端',

  /* ── How a session ended ─────────────────────────────────────────────── */
  'sessionEnd.signed_out': '已退出登录',
  'sessionEnd.password_changed': '因修改密码而结束',
  'sessionEnd.account_deleted': '账号已删除',
  'sessionEnd.account_disabled': '账号已停用',
  'sessionEnd.revoked_by_admin': '由管理员结束',
  'sessionEnd.superseded': '已在其他设备登录',

  /* ── Row actions ─────────────────────────────────────────────────────── */
  'col.actions': '操作',
  'action.noneAvailable': '该行没有可执行的操作',
  'action.noPermission': '您的角色无权执行此操作',
  'action.reroll': '重新生成',
  'bets.cannotEditSettled': '已退款或已作废的注单无法修改',
  'draws.cannotVoid': '仅未结算的期号可作废',
  'draws.cannotSetResult': '已作废的期号没有可设定的结果',
  'sessions.cannotRevoke': '该会话已经结束',

  /* ── Rules booklet and upcoming rounds ───────────────────────────────── */
  'notifications.readOnlyHint': '只读 —— 您的角色无权修改这些文案。',
  'rules.title': '规则与教学',
  'rules.explain': '玩家端规则弹窗的正文 —— 介绍游戏是什么、该怎么玩。与游戏页的赔率和限额分开管理：那些数值由客户端从游戏设置读取，若在此处再写一遍，下次调整赔率时两边就会对不上。两种语言一同发布。',
  'rules.version': '版本 {version}',
  'rules.versionWarning': '保存即发布新版本，所有玩家（包括已阅读过的）都会再次看到该弹窗，无法静默修正。',
  'rules.languageTabs': '手册语言',
  'rules.docTitle': '手册标题',
  'rules.docTitleHint': '显示在弹窗最上方的标题',
  'rules.intro': '开头段落',
  'rules.introHint': '显示在各章节之上 —— 玩家如果只看一段，就是这一段',
  'rules.sections': '章节（{count}）',
  'rules.sectionNo': '第 {number} 节',
  'rules.addSection': '新增章节',
  'rules.noSections': '尚未添加章节，弹窗将只显示标题和开头段落。',
  'rules.heading': '小标题',
  'rules.body': '正文',
  'toast.rulesPublished': '通知文案已保存，规则手册已发布为版本 {version}。',
  'upcoming.title': '即将开奖号码',
  'upcoming.explain': '开奖号码会提前生成并排队，便于您提前查看并在开奖前修改。第一行对应正在倒计时的那一期；其余为预估值，若某期被作废或游戏暂停则会变动。',
  'upcoming.round': '期号',
  'upcoming.drawsAt': '开奖时间',
  'upcoming.numbers': '号码',
  'upcoming.origin': '来源',
  'upcoming.live': '进行中',
  'upcoming.estimated': '预估',
  'upcoming.manual': '手动设定',
  'upcoming.auto': '系统生成',
  'upcoming.stale': '超出当前号码范围 —— 该期将改为现场生成号码。请修改或重新生成。',
  'upcoming.reroll': '重新生成',
  'upcoming.paused': '游戏已暂停 —— 时间仅供参考',
  'upcoming.empty': '队列中暂无号码。',
  'upcoming.editTitle': '第 #{drawNo} 期的号码',
  'upcoming.editLive': '这是玩家正在投注的那一期（{period}），保存后即为该期的开奖号码。',
  'upcoming.editProjected': '该期尚未开始。若某期被作废或游戏暂停，它最终对应的期号会变动。',
  'upcoming.rangeHint': '共 {count} 个号码，每个均在 {min} 到 {max} 之间。',
  'upcoming.reasonPlaceholder': '为上线演示预设的结果',
  'upcoming.save': '保存号码',
  'toast.upcomingSaved': '开奖号码已保存',
  'toast.upcomingSavedDetail': '第 #{drawNo} 期将按您设定的号码开奖。',
  'toast.upcomingRerolled': '号码已重新生成',
  'toast.upcomingRerolledDetail': '第 #{drawNo} 期已恢复为系统生成的号码。',
  'error.upcoming': '无法修改排队中的号码',

  /* ── Settings ────────────────────────────────────────────────────────── */
  'settings.yourAccount': '你的账户',
  'settings.changePassword': '修改密码',
  'settings.current': '当前密码',
  'settings.new': '新密码',
  'settings.confirm': '确认新密码',
  'settings.mismatch': '两次输入的密码不一致',

  /* ── Audit log ───────────────────────────────────────────────────────── */
  'audit.title': '操作日志',
  'audit.empty': '暂无记录',
  'audit.emptyDetail': '管理员的操作会实时显示在此处。',
  'audit.scopedToYou': '此处仅显示您本人的操作记录。只有超级管理员可查看全部操作员的记录。',
  'audit.note': '所有管理员操作均记录于此。日志只可追加 — 接口与后台均无修改或删除入口。',
  'audit.noChange': '无字段变更',
  'audit.filter.anyAction': '全部操作',
  'audit.filter.anyOperator': '全部操作员',
  'audit.filter.targetId': '对象 ID',
  'audit.filter.recordId': '记录 ID',

  /* ── Credit dialog ───────────────────────────────────────────────────── */
  'credit.topUp': '充值',
  'credit.setBalance': '设置余额',
  'credit.withdraw': '提现',
  'credit.operation': '额度操作',
  'credit.title': '{mode} — {name}',
  'credit.currentBalance': '当前余额',
  'credit.afterTopUp': '充值后',
  'credit.afterWithdrawal': '提现后',
  'credit.newBalance': '新余额',
  'credit.amountToAdd': '充值金额',
  'credit.amountToDeduct': '扣除金额',
  'credit.exactHint': '这是最终金额，而非增减额度。',
  'credit.topUpBlurb': '用于收到款项后为玩家增加额度。将在玩家钱包记录中显示为一笔充值，而非难以理解的调整。',
  'credit.withdrawBlurb': '用于向玩家付款后扣除额度。将在玩家钱包记录中显示为一笔提现，而非难以理解的调整。',
  'credit.setBlurb': '直接把额度设为你输入的金额。若确有资金进出，请改用充值或提现 — 它们会在玩家记录中显示为存款与出款。',
  'credit.reasonRef': '原因或凭证号',
  'credit.topUpPlaceholder': '已收到银行转账，凭证号 8841-2290',
  'credit.withdrawPlaceholder': '已通过银行转账付款，凭证号 4471',
  'credit.setPlaceholder': '重复入账后更正',
  'credit.recordTopUp': '确认充值',
  'credit.recordWithdrawal': '确认提现',
  'credit.tooMuch': '超出可用余额 {amount}',
  'credit.nonNegative': '请输入有效的非负金额',
  'credit.willCredit': '此操作将入账 {amount}，并作为调整记入账目。',
  'credit.willDebit': '此操作将扣除 {amount}，并作为调整记入账目。',

  /* ── Status filter options ──────────────────────────────────────── */
  'statusOption.any': '全部状态',
  'statusOption.pending_review': '待审核',
  'statusOption.active': '正常',
  'statusOption.banned': '已封禁',
  'statusOption.rejected': '已拒绝',

  /* ── Review queue ────────────────────────────────────────────────────── */
  'review.approveBlurb': '通过审核后账户即可启用，{name} 便能登录并下注。初始余额为选填，将与其他额度一样记入账目与操作日志。',
  'review.thisPlayer': '该玩家',
  'review.rejectBlurb': '账户将保留但无法登录。原因会记录在该账户及操作日志中。',
  'review.approvedToast': '账户已通过',
  'review.approvedDetail': '{name} 现在可以登录并下注。',
  'review.rejectedToast': '注册已拒绝',
  'review.rejectedDetail': '{name} 无法登录。',

  /* ── Review queue errors ─────────────────────────────────────────────── */
  'review.approveError': '无法通过该账户',
  'review.rejectError': '无法拒绝该注册',
  'review.creditsOnApproval': '通过后入账 {amount}',
  'review.noOpening': '留在 0.00 表示不入账',

  /* ── Credit dialog errors ────────────────────────────────────────────── */
  'credit.failed': '额度变更未能完成',
  'credit.topUpToast': '充值已记录',
  'credit.topUpDetail': '已入账 {amount} — {name} 当前余额 {balance}。',
  'credit.withdrawToast': '提现已记录',
  'credit.withdrawDetail': '已扣除 {amount} — {name} 当前余额 {balance}。',
  'credit.setToast': '余额已更新',
  'credit.setDetail': '{name} 已设为 {balance}（{delta}）。',

  /* ── Bets ────────────────────────────────────────────────────────────── */
  'bets.filter.placedFrom': '下注起始',
  'bets.filter.placedTo': '下注结束',
  'bets.updatedToast': '注单已更新',
  'bets.stakeDelta': '投注额 {amount}。',
  'bets.nowStatus': '当前状态：{status}。',
  'bets.nowStatusPaying': '当前状态：{status}，派彩 {amount}。',
  'bets.updateError': '无法更新该注单',
  'bets.removedToast': '注单已删除',
  'bets.removedDetail': '投注额已退回，派彩（如有）已冲正。',
  'bets.removeError': '无法删除该注单',

  /* ── Player dialogs ──────────────────────────────────────────────────── */
  'players.whatGoesWithThem': '一并删除的内容',

  /* ── Administrator roles ─────────────────────────────────────────────── */
  'admins.roleScope.support': '审核队列与只读页面',
  'admins.roleScope.admin': '余额、注单、开奖与设置',
  'admins.roleScope.superadmin': '全部权限，包括员工账户',

  /* ── Administrator filters ───────────────────────────────────────────── */
  'admins.anyRole': '全部角色',

  /* ── Option lists ────────────────────────────────────────────────────── */
  'ledger.anyType': '全部类型',
  'txStatus.success': '成功',
  'txStatus.processing': '处理中',
  'txStatus.pending': '待处理',
  'txStatus.failed': '失败',
  'txStatus.rejected': '已拒绝',
  'drawStatus.open': '进行中',
  'drawStatus.locked': '已封盘',
  'drawStatus.drawn': '已开奖',
  'drawStatus.settled': '已结算',
  'drawStatus.void': '已作废',
  'draws.filter.houseWon': '庄家盈利',
  'draws.filter.houseLost': '庄家亏损',
  'draws.filter.breakEven': '持平',
  'admins.anyState': '全部状态',
  'referrals.anyState': '全部状态',
  'referrals.searchCode': '搜索推荐码',

  /* ── Toasts, errors and misc ─────────────────────────────────────────── */
  'toast.playerCreated': '玩家已创建',
  'toast.playerUpdated': '玩家已更新',
  'toast.playerDeleted': '玩家已删除',
  'toast.adminCreated': '管理员已创建',
  'toast.adminUpdated': '管理员已更新',
  'toast.adminDeleted': '管理员已删除',
  'toast.codeCreated': '推荐码已创建',
  'toast.codeUpdated': '推荐码已更新',
  'toast.codeDeleted': '推荐码已删除',
  'toast.rowUpdated': '账目已更新',
  'toast.rowDeleted': '账目已删除',
  'toast.balanceCorrected': '余额已校正为 {amount}。',
  'toast.gameUpdated': '游戏已更新',
  'toast.gameSettingsNote': '下一期将使用这些设置。',
  'toast.tileCreated': '图块已新增',
  'toast.tileCreatedDetail': '{name} 已进入游戏大厅。',
  'toast.tileDeleted': '图块已删除',
  'toast.tileDeletedDetail': '{name} 已从游戏大厅移除。',
  'toast.tileHidden': '图块已隐藏',
  'toast.tileHiddenDetail': '玩家将不再收到该图块。可随时在本页恢复显示。',
  'toast.tileShown': '图块已恢复',
  'toast.tileShownDetail': '它已回到玩家游戏大厅。',
  'toast.gameStarted': '游戏已启动',
  'toast.gamePaused': '游戏已暂停',
  'toast.historyCreated': '历史已生成',
  'toast.notificationsSaved': '通知设置已保存',
  'toast.notificationsDetail': '玩家下次请求时即可看到新文案。',
  'toast.passwordChanged': '密码已修改',
  'toast.passwordDetail': '请使用新密码重新登录。',
  'toast.resultSet': '结果已录入',
  'toast.resultChanged': '结果已更改',
  'toast.noBets': '该期没有任何注单。',
  'toast.roundVoided': '期次已作废',
  'toast.roundDeleted': '期次已删除',
  'toast.copyFailed': '复制失败',
  'toast.copyManually': '请手动选中推荐码并复制。',
  'error.createPlayer': '无法创建该玩家',
  'error.updatePlayer': '无法更新该玩家',
  'error.deletePlayer': '无法删除该玩家',
  'error.createAdmin': '无法创建该管理员',
  'error.updateAdmin': '无法更新该管理员',
  'error.deleteAdmin': '无法删除该管理员',
  'error.createCode': '无法创建该推荐码',
  'error.updateCode': '无法更新该推荐码',
  'error.deleteCode': '无法删除该推荐码',
  'error.updateRow': '无法更新该账目',
  'error.deleteRow': '无法删除该账目',
  'error.saveGame': '无法保存该游戏',
  'error.deleteGame': '无法删除该图块',
  'error.changeGame': '无法变更该游戏',
  'error.buildHistory': '无法生成记录。',
  'error.changePassword': '无法修改密码',
  'error.save': '保存失败',
  'error.setResult': '无法录入开奖结果',
  'error.voidRound': '无法作废该期',
  'error.deleteRound': '无法删除该期',
  'error.endSession': '无法结束该会话',
  'error.endSessions': '无法结束这些会话',
  'error.passwordsDiffer': '两次输入的密码不一致',
  'error.minCode': '至少 4 个字符',
  'error.atLeastMinimum': '不得低于最小值',
  'games.startedFromConsole': '由后台启动',
  'games.pausedFromConsole': '由后台暂停',
  'dashboard.noActivity': '暂无动态',
  'dashboard.openingNumbers': '开奖号码',

  /* ── Prose ───────────────────────────────────────────────────────────── */
  'players.deleteNote': '相关记录将一并删除而非保留孤立数据 — 留存会使报表失真。此次删除仍会记录在操作日志中，且无法撤销。',
  'admins.selfLocked': '这是你自己的账户，角色与状态已锁定 — 修改任一项都可能让组织失去访问权限。如需改密码请前往“设置”。',
  'admins.deleteNote': '将删除该账户。其在操作日志中的记录会保留 — 操作痕迹不随账户一并消失。',
  'bets.removeNote': '历史不会被删除。该注单将标记为作废，并以冲正分录校正余额，账目仍然平衡。',
  'games.tilesNote': '大厅中的其余图块。这些数据由你在此维护 — 可新增、改名、调整排序、隐藏或删除。不可投注的图块仍会展示给玩家，只是点击不会进入游戏，这是展示上的安排，而非失效的链接。',
  'ledger.amountFixed': '金额刻意不可修改 — 可被改写的账目就不再是账目。如需调整余额，请在玩家页面操作。',
  'notifications.reviewNote': '玩家提交注册后会看到此提示，在审核通过前尝试登录时也会再次出现。',
  'notifications.supportNote': '玩家点击“充值”和“提现”后的跳转位置。系统没有存取款接口 — 两者都会打开此链接。',
  'referrals.deleteNote': '玩家列表将不再显示他们使用过的推荐码。',
  'referrals.disableInstead': '改为停用则会拒绝新注册，同时保留这些历史记录。',
  'settings.currentRequired': '即使已登录仍需输入当前密码 — 这样才能防止他人利用无人看管的屏幕。',
  'testPlayers.realNote': '这里创建的账户就是真实玩家 — 会和其他玩家一样出现在报表、账目与玩家列表中。',
  'testPlayers.historyNote': '生成的历史会针对已结算的期次写入真实注单。',
  'testPlayers.buildNote': '为该玩家在已结算的期次中补下注单。投注额从钱包扣除、彩金计入钱包，账目始终保持一致。',
  'testPlayers.buildTitle': '生成历史 — {name}',
  'draws.voidNote': '取消该期并退还其全部投注额。已结算的期次无法作废 — 请改为更改其开奖结果。',
  'players.statusNow': '状态已变更为 {status}。',
  'common.sortBy': '按 {field} 排序',
  'referrals.alreadyUsed': '已使用 {count} 次',

  /* ── Prose, part two ─────────────────────────────────────────────────── */
  'bets.edit.drawnNote': '该期已开奖。保存后将按开奖号码重新判定此注单，并同步调整玩家余额。',
  'bets.edit.stillOpen': '该期尚未截止。投注额的差额将像其他余额变动一样计入玩家钱包。',
  'draws.paidWarn': '该期已完成派彩。更改号码将重新判定其全部注单。',
  'draws.paidExplain': '派彩差额将以调整方式计入钱包：原本中奖而现在未中的注单会退回款项，原本未中而现在中奖的注单会全额派彩。余额始终保持一致，玩家也能在记录中看到变动。',
  'draws.freshExplain': '该期所有注单将按这些号码判定并立即派彩。',
  'ledger.amountFixedNote': '金额刻意不可修改 — 数字可随意改写的账目就不再是账目。备注、状态与发生时间可以编辑；如需改动金额，请删除此条或新增一笔调整。',
  'notifications.reviewExplain': '玩家提交注册后会看到此提示，在审核通过前尝试登录时也会再次出现。这是期间唯一的提示，应说明接下来会发生什么。',
  'notifications.supportExplain': '将在三处向玩家显示：注册后的弹窗、充值与提现。系统没有支付网关——两条资金路径最终都由客服处理，因此这段文字与链接就是全部流程。玩家端通过 `GET /contact` 读取，保存后会即时推送至所有在线客户端。',
  'referrals.signupsNote': '有 {count} 位玩家使用该推荐码注册。他们的账户会保留，但引用将失效，玩家列表也不再显示其使用的推荐码。',
  'referrals.disableExplain': '改为停用则会拒绝新注册，同时保留可查的历史记录 — 如需如此，请使用“编辑 → 状态”。',

  /* ── Prose, part three ───────────────────────────────────────────────── */
  'settings.currentExplain': '即使已登录仍需输入当前密码 — 这样才能避免无人看管的终端变成账户的永久移交。修改后你将被登出。',
  'testPlayers.realExplain': '这里创建的账户就是真实玩家 — 会和自行注册的玩家一样出现在报表、汇总与玩家列表中。生成的历史会针对已结算的期次写入真实注单，每一笔投注与派彩都经过钱包，因此余额之后仍然平衡。',
  'testPlayers.buildExplain': '为该玩家在已结算的期次中补下注单。投注额从钱包扣除、彩金计入钱包，账目始终保持一致 — 玩家只是多了一段过往记录。',
  'draws.voidExplain': '取消该期并退还其全部投注额。已结算的期次无法作废。',
  'draws.betsOnRound': '{count} 注',
  'draws.currentlyOnRound': '目前在该期上。',
  'draws.deleteRoundTitle': '删除第 {drawNo} 期',
  'draws.voidRoundTitle': '作废第 {drawNo} 期',

  /* ── Final strings ───────────────────────────────────────────────────── */
  'betStatus.placed': '已下注',
  'betStatus.won': '中奖',
  'betStatus.lost': '未中',
  'betStatus.refunded': '已退款',
  'betStatus.voided': '已作废',
  'reports.turnoverExplain': '统计已结算注单的投注额 — 进行中期次的注单不计入，因为结果尚未可知。',
  'reports.profitExplain': '为投注总额减去派彩。所有玩法均为 1.98× 的均等赔付，理论利润率为 1%。短周期会在该值上下波动，长周期则应趋近于此。',
  'reports.testExplain': '默认不计入。计入后会把生成的演示数据混进统计，这对检查生成器有用，但对其他用途会造成误导。',
  'reports.marginOfTurnover': '占投注总额的 {value}%',

  /* ── Void the running round ──────────────────────────────────────────── */
  'games.voidRound': '作废本期',
  'games.voidTitle': '作废进行中的期次',
  'games.voidBlurb': '将取消 {period} 并立即按当前设置开出新一期。被取消期次的所有投注都会全额退还，不会让任何注单对着永远不会开出的结果。',
  'games.voidWhy': '适用于修改上方设置之后 — 设置仅对之后开出的期次生效，否则进行中的这一期仍会沿用旧配置。',
  'games.voidReasonPlaceholder': '已缩短开奖间隔 — 按新设置重新开始',
  'games.voidSubmit': '作废并重开',
  'games.voidBets': '本期目前有 {count} 注',
  'games.voidNoRound': '当前没有进行中的期次，无需作废。',
  'toast.roundVoidedRestarted': '期次已作废并重开',
  'toast.roundVoidedDetail': '已退还 {count} 注。{period} 现已开盘。',

  /* ── Straight number bets ────────────────────────────────────────────── */
  'bet.number': '号码',
  'bets.edit.pickedNumber': '号码',
  'bets.edit.pickedRange': '号码 — {min} 至 {max}',
  'bets.edit.pickedError': '请在本期号码范围内选择',
  'bets.edit.numberOdds': '同一半区任意号码皆中 · 赔付 {odds}',

  /* ── Registration details, player history search and timestamp edits ─── */
  'field.bankName': '银行',
  'field.bankAccountNo': '银行账号',
  'field.bankLoadFailed': '无法加载银行列表，请重新打开此窗口再试。',
  'field.bankUnlisted': '{name}（不在当前列表中）',
  'players.detail.registration': '注册资料',
  'players.detail.registrationNote': '玩家注册时填写的全部内容。',
  'players.filter.player': '玩家',
  'players.filter.playerPlaceholder': '按用户名搜索',
  'bets.edit.placedAt': '投注时间',
  'bets.edit.placedAtHint': '仅调整该注单在记录中的时间位置，投注额、赔率与结果均不受影响。',
  'bets.edit.placedAtError': '请输入有效的日期与时间',
  'ledger.edit.happenedAt': '日期与时间',
  'ledger.edit.happenedAtHint': '该笔资金变动发生的时间。金额与当时的余额快照保持不变。',
  'ledger.edit.happenedAtError': '请输入有效的日期与时间',
  /* ── Generated history: the profile form ─────────────────────────────── */
  'day.short.0': '日',
  'day.short.1': '一',
  'day.short.2': '二',
  'day.short.3': '三',
  'day.short.4': '四',
  'day.short.5': '五',
  'day.short.6': '六',
  'history.section.when': '投注时间',
  'history.section.how': '投注方式',
  'history.from': '起始日期',
  'history.to': '结束日期',
  'history.pattern': '投注规律',
  'history.pattern.weekdays': '每周固定星期',
  'history.pattern.interval': '每隔几天',
  'history.days': '投注日',
  'history.daysHint': '选择该玩家下注的星期几。',
  'history.daysError': '请至少选择一天',
  'history.intervalDays': '每隔几天投注',
  'history.intervalDays.suffix': '天',
  'history.intervalDaysHint': '投注日之间相隔的天数，每次随机抽取 — 填 4 至 6 表示间隔在 4 到 6 天之间浮动，两端填 6 则固定每 6 天一次。',
  'history.betsPerDay': '每个投注日的注数',
  'history.betsPerDayHint': '两端填相同数值即为固定注数。',
  'history.rangeError': '后一个数值不得小于前一个',
  'history.minStake': '最小投注额',
  'history.maxStake': '最大投注额',
  'history.winRate': '胜率',
  'history.winRateHint': '精确而非近似：70% 即恰好七成注单中奖，且每一注仍按该期真实号码判定。',
  'history.depositMin': '最小存款额',
  'history.depositMax': '最大存款额',
  'history.autoTopUp': '存款不足时自动提高当天存款',
  'history.autoTopUpHint': '当天的下注或结尾的提款超出抽取的存款金额时。关闭则直接拒绝此配置。',
  'history.withdrawalShare': '每次提取盈利比例',
  'history.withdrawalShareHint': '每笔提款 = 当天存款 + 盈利的此比例，因此一定大于存款。',
  'history.withdrawalFloor': '最低提款',
  'history.withdrawalFloorHint': '每个存款日至少提款这个金额。',

  /* ── Generated history: the dialogs ──────────────────────────────────── */
  'history.title': '{name} 的游戏记录',
  'history.explain': '在所选日期范围内写入真实的投注与钱包记录。此处全部为精确值：笔数与胜率在写入前已针对整个周期计算完毕，填 70% 胜率就是正好 70%。每个投注日都是 存款 → 下注 → 提款。投注额与派彩同样经由钱包流转，生成后余额仍与账目相符。',
  'history.enable': '为该玩家生成投注记录',
  'history.enableHint': '在此配置后会随账号一并保存，之后可随时运行或调整。',
  'history.generateNow': '账号创建后立即生成',
  'history.preview': '预览',
  'history.previewTitle': '将要生成的内容',
  'history.previewBets': '共 {bets} 注，横跨 {days} 个投注日，其中 {wins} 注中奖',
  'history.previewDeposits': '{count} 笔存款',
  'history.previewWithdrawals': '{count} 笔提款',
  'history.save': '保存配置',
  'history.saveAndRun': '保存并重新生成',
  'history.run': '立即生成',
  'history.clear': '清除已生成记录',
  'history.clearConfirm': '确定移除此前生成的投注与钱包记录吗？账号自身的真实游戏记录不受影响。',
  'history.replaceNote': '重新生成会替换先前生成的内容，使记录与配置一致，而非两者并存。',
  'history.lastRun': '上次生成于 {when}',
  'history.neverRun': '尚未生成',
  'history.noProfile': '未设置记录配置',
  'history.working': '正在写入记录 — 范围较长时需要稍候。',
  'history.section.money': '存款与提款',
  'history.moneyExplain': '存款日以一笔存款开始、以一笔大于该存款的提款结束；其余投注日只有下注与中奖。没有下注的日子两者都没有。',
  'history.openingBalanceHint': '会并入第一笔生成的存款，使记录按时间顺序并以提款结束。',
  'history.cashFlow': '每日存提款几率（%）',
  'history.cashFlowHint': '每个投注日各自按此几率抽取。抽中：存款 → 下注与中奖 → 提款，且提款一定大于存款。未抽中：只有下注与中奖。100% 即每天都有。第一天一定存款；余额不足下注的日子也会存款。',
  'history.cashFlowError': '请输入 0 到 100',
  'history.order.cashDays': '存款日（{percent}% 几率）',
  'history.order.betDays': '否则（{percent}% 几率）',
  'history.order.hint': '每个投注日都按以上其中一种顺序。未中奖的下注没有中奖记录。',
  'history.section.day': '每日流程',
  'history.window': '活动时段',
  'history.windowHint': '所有下注、中奖、存款与提款记录都只会落在这个时段内。',
  'history.windowOvernight': '跨越午夜 — 该时段归属于开始的那一天。',
  'history.windowError': '请输入两个不同的时间',
  'history.timeZone': '时区',
  'history.timeZoneHint': '以上时间按此时区计算，而不是服务器时区。',
  'history.settle': '中奖记录出现时机',
  'history.settle.each': '每注之后',
  'history.settle.together': '先下注，后中奖',
  'history.gap': '记录间隔',
  'history.gap.suffix': '分钟',
  'history.gapHint': '另加等待每期开奖的时间。如当天放不进活动时段，会自动缩短。',
  'history.step.deposit': '存款',
  'history.step.withdraw': '提款',
  'history.step.bet': '下注{n}',
  'history.step.win': '中奖{n}',
  'history.previewWindow': '全部在 {start} 至 {end} 之间（{zone}）',
  'history.sampleTitle': '前几天的记录预览',
  'history.sample.betWon': '下注（中）',
  'history.sample.betLost': '下注（未中）',
  'history.sample.win': '中奖',
  'toast.historyBuilt': '记录已生成',
  'toast.historyBuiltDetail': '共 {bets} 注（{won} 注中奖）、{deposits} 笔存款、{withdrawals} 笔提款。当前余额 {balance}。',
  'toast.historyReplaced': '已替换先前的 {bets} 注与 {rows} 条钱包记录。',
  'toast.historyCleared': '已清除生成的记录',
  'toast.historyClearedDetail': '已移除 {bets} 注、{rows} 条钱包记录与 {rounds} 期开奖。',
  'toast.profileSaved': '配置已保存',
  'status.backfilled': '生成',
};
